import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  Eventcalendar,
  momentTimezone,
  Page,
} from "@mobiscroll/react";
import { IconStatus, singleEventSkeleton } from "../../constants";
import BookingScheduleStatus from "../book-equipment/calendar-timeline/BookingScheduleStatus";
import { OwcButton, OwcIcon, OwcModalDialog, OwcTypography, OwcHyperlink, OwcTooltip } from "@one/react";
import { connect, useSelector } from "react-redux";
import BackdropPortal from "../../components/shared/BackdropPortal";
import PopoverBookingTitle from "../../components/shared/CommonPopOverModal/PopoverBookingTitle";
import PopOverModal from "../../components/shared/CommonPopOverModal/PopOverModal";
import useDialog from "../../utils/hooks/useDialog";
import PopoverBookingStatusDetail from "../../components/shared/CommonPopOverModal/PopoverBookingStatusDetail";
import { buildProjectsLabel, valueOrEmpty } from "../../utils/helpers/text";
import moment from "moment";
import { GET_BOOKING_DATA_BY_SITE, GET_BOOKING_ENTRIES_BY_BOOKING_ID } from "../../gql/bookingapi";
import { getAllData } from "../../utils/helpers/fetching";
import { compose, withApollo } from "react-apollo";
import { useHistory } from "react-router-dom";
import {
  changeActionTypeToDisplay,
  getBookingCategory,
  isActionType,
  formatBookingForDisplay,
} from "../../components/shared/helpFunctions";
import "./BookingGrid.scss";
import { cloneDeep } from "lodash";
import { prepareBookingForDuplication } from "../../utils/helpers/data";
import { BookingContext } from "../booking/context";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { loadPendingReservations as loadPendingReservationsAction } from "../booking-calendars/redux/actions";
import { getRepetitionIcon } from "../../utils/helpers/getRepetitionIcon";
import getRRuleDisplayText from "../../utils/helpers/text/transformRRuleToText";
import DeleteBooking from "../DeleteBooking";
import { isBookingOngoingInTimezone } from "../../utils/helpers/dropBooking";

const MainViewStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
`;

const CalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const BookingGrid = ({
  client,
  eventID = null,
  _urlAction = null,
  loadPendingReservations,
  shouldFetchData = false,
}) => {
  const dataTimeZone = "utc";
  const [myEvents, setEvents] = useState([]);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const user = useSelector((store) => store.user);
  const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
  const { openDialog, ...dialogProps } = useDialog();
  const [loading, setLoading] = useState(false);
  const displayTimeZone = user.timezone;
  const [popoverObject, setPopoverObject] = useState({
    equipmentData: {},
    eventData: {},
  });
  const currentDateRangeRef = useRef(null);
  const isFetchingEventsRef = useRef(false);
  const [dropType, setDropType] = useState("cancel");
  const [isDropBooking, setIsDropBooking] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [status, setStatus] = useState(false);
  const [bookingIdForAction, setBookingIdForAction] = useState(null);

  // States for approve/reject actions from URL
  const { dispatchAction } = useContext(PaginationContext);
  const { dispatchAction: bookingDispatchAction } = useContext(BookingContext);
  const view = useMemo(() => {
    return {
      schedule: { type: "week", allDay: false },
    };
  }, []);
  const onEventClick = useCallback((event) => {
    let eventData = {
      ...event?.event,
      reservedForEmails: event?.event?.bookedUsers?.map((k) => k?.email),
    };
    openDialog();
    setPopoverObject({
      equipmentData: event?.event,
      eventData: eventData,
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Render Custom events for timeline
   * @returns {Object} - template for Custom events
   */

  const resolveProjectText = (data) => {
    const row = data || {};
    return buildProjectsLabel({
      rowRedPandaProjects: row.redPandaProjects,
      userRedPandaProjects: user?.redPandaProjects,
      projectId: row.project,
    });
  };

  const renderMyBookingModalActions = () => {
    const eventData = popoverObject?.eventData;
    if (!eventData || Object.keys(eventData).length === 0) {
      return null;
    }

    const isEndNowAllowed =
      eventData?.active !== false && isBookingOngoingInTimezone(eventData, user?.timezone || "UTC");

    if (!isEndNowAllowed) {
      return null;
    }

    return (
      <OwcButton
        variant="secondary"
        onClick={() => {
          setDropType("end");
          setBookingIdForAction(eventData?.id || eventData?.bookingId || null);
          setIsDropBooking(true);
          setIsDialogOpen(true);
        }}
      >
        End now
      </OwcButton>
    );
  };
  const editBooking = async (editData) => {
    const data = cloneDeep(editData);
    const currentTimeString = moment.tz(user.timezone).utc().format();
    dispatchAction({
      type: "fetching",
      payload: true,
    });
    //fetch data from grpahql
    const { items } = await getAllData({
      client,
      query: GET_BOOKING_ENTRIES_BY_BOOKING_ID,
      drillData: true,
      fetchPolicy: "no-cache",
      variables: {
        bookingId: data.id,
        nextToken: null,
        filter: { active: { eq: true } },
      },
      dataPath: ["data", "bookingEntriesByBookingId"],
    });

    if (items.length > 0) {
      const keyArray = Object.keys(singleEventSkeleton);
      data["bookingEntries"] = items.map((item) => {
        let tempObj = {};
        keyArray.forEach((key) => {
          tempObj = {
            ...tempObj,
            [key]: item[key] ?? null,
          };
        });
        //extra keys
        tempObj = {
          ...tempObj,
          inventoryId: item.resource,
          equipment: item.equipment,
          title: item.type,
          description: item?.description,
          elnId: item?.elnId,
          project: item?.project,
          redPandaProjects: item?.redPandaProjects,
          createdBy: item?.createdBy,
          reservedForEmails: item?.reservedForEmails,
        };
        return tempObj;
      });
      if (items[0].elnId) data.elnId = items[0].elnId;
    }
    const isDuplicate = prepareBookingForDuplication(data);
    bookingDispatchAction({
      type: "editBookingData",
      payload: {
        isEditMode: true,
        isDuplicate,
        forceMyBookingsRedirect: true,
        editBookingData: data,
        isOngoing: currentTimeString > data.dateFrom && currentTimeString < data.dateTo,
      },
    });

    const tempBookingEntries = data?.bookingEntries.map((item) => {
      return {
        ...item,
        inventoryId: item.resource,
        isEditEventFlag: true,
        isOngoingEventFlag:
          (currentTimeString > item.end && currentTimeString > item.start) ||
          (currentTimeString > item.start && currentTimeString < item.end),
        editable: currentTimeString < item.end,
        title: item?.type,
      };
    });
    loadPendingReservations(tempBookingEntries);
    if (data) history.push("/");
  };

  const renderScheduleEvent = useCallback((data) => {
    let labelColor = IconStatus?.filter((k) => k.text === getBookingCategory(data?.original?.type));
    const recurranceText = getRRuleDisplayText(
      data?.original?.repetitionPattern,
      new Date(data?.original?.dateFrom),
      displayTimeZone,
    );

    return (
      <div
        className="md-custom-event-cont"
        style={{
          width: "100%",
          height: "100%",
          // minHeight: "415px",
          background: data?.color,
        }}
      >
        <div className="md-custom-event-wrapper">
          <div className="md-custom-event-category">
            <OwcIcon
              type="outlined"
              name={data?.original?.icon}
              style={{
                minWidth: 40,
                minHeight: 40,
                borderRadius: "5px",
                marginRight: "5px",
                fontSize: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid white",
                color: labelColor?.[0]?.color,
              }}
            />
            <div className="md-custom-event-title">
              <OwcTypography variant="badge" style={{ color: labelColor?.[0]?.color }}>
                <strong>{getBookingCategory(data?.original?.type)}</strong>
              </OwcTypography>

              <div className="md-custom-event-time" style={{ color: labelColor?.[0]?.color, display: "flex" }}>
                <OwcTypography variant="caption">
                  <div style={{ display: "flex", paddingLeft: "5px" }}>
                    {recurranceText && <>{getRepetitionIcon(recurranceText)}</>}
                    {data?.start === "" && data?.end === "" ? (
                      <> {data?.allDayText?.replaceAll(":00", "")}</>
                    ) : (
                      <>
                        {data?.start?.replace(":00", "")} - {data?.end?.replace(":00", "")}
                      </>
                    )}
                  </div>
                </OwcTypography>
              </div>
            </div>
          </div>
          <div className="md-custom-event-details">
            <div className="md-custom-event-description">
              <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                Description
              </OwcTypography>
              <OwcTypography variant="subtitle2" style={{ color: labelColor?.[0]?.textColor }}>
                {data?.original?.description}
              </OwcTypography>
            </div>

            <div className="md-custom-event-description">
              <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                {data?.original?.redPandaProjects?.length ? "Projects" : "Project"}
              </OwcTypography>

              {(() => {
                const projectText = resolveProjectText(data?.original);
                const tooltipId = `booking-project-${data?.original?.id}`;
                const projectLabelStyle = {
                  maxWidth: "120px",
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                };
                return (
                  <>
                    <OwcTypography
                      variant="subtitle2"
                      id={tooltipId}
                      style={{ ...projectLabelStyle, color: labelColor?.[0]?.textColor }}
                    >
                      {projectText}
                    </OwcTypography>

                    {projectText && projectText !== "-" && (
                      <OwcTooltip anchor={tooltipId}>
                        <span style={{ whiteSpace: "pre-line" }}>{projectText?.replaceAll(", ", "\n")}</span>
                      </OwcTooltip>
                    )}
                  </>
                );
              })()}
            </div>

            {isActionType(data?.original?.type) && (
              <div>
                <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                  Action Type
                </OwcTypography>
                <OwcTypography variant="subtitle2" style={{ color: labelColor?.[0]?.textColor }}>
                  {valueOrEmpty(changeActionTypeToDisplay(data?.original?.type), false, "-")}
                </OwcTypography>
              </div>
            )}
            <div className="md-custom-event-description">
              <OwcTypography variant="caption" style={{ color: labelColor?.[0]?.color }}>
                Number of equipment
              </OwcTypography>
              <OwcTypography variant="subtitle2" style={{ color: labelColor?.[0]?.textColor }}>
                {data?.original?.noEquipment}
              </OwcTypography>
            </div>
          </div>
        </div>
      </div>
    ); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Render header for timeline
   * @returns {React.FC} - template for event header
   */
  const renderMyHeader = () => {
    return (
      <>
        <CalendarNav className="md-work-week-nav" />
        <CalendarPrev className="md-work-week-prev" />
        <CalendarToday className="md-work-week-today" />
        <CalendarNext className="md-work-week-next" />
      </>
    );
  };
  const resolveEventIdentity = (eventData) => {
    return eventData?.bookingId || eventData?.id || eventData?.entryId;
  };

  const buildGetBookingDataFilter = useCallback(
    ({ dateFrom, dateTo }) => {
      return JSON.stringify({
        and: [
          { active: { eq: true } },
          {
            and: [{ dateFrom: { le: dateTo } }, { dateTo: { ge: dateFrom } }],
          },
          {
            or: [{ reservedForUsers: { contains: user.user } }, { assistent: { contains: user.user } }, { createdBy: { eq: user.user } }],
          },
        ],
      });
    },
    [user.user],
  );

  const refreshPopoverObjectWithEvent = useCallback((prev, newModifiedData) => {
    const currentIdentity = resolveEventIdentity(prev?.eventData);
    if (!currentIdentity) {
      return prev;
    }

    const refreshedEvent = newModifiedData.find((candidate) => resolveEventIdentity(candidate) === currentIdentity);

    if (!refreshedEvent) {
      return prev;
    }

    return {
      equipmentData: refreshedEvent,
      eventData: {
        ...refreshedEvent,
        reservedForEmails: refreshedEvent?.bookedUsers?.map((k) => k?.email),
      },
    };
  }, []);

  const loadCalendarEvents = useCallback(
    async ({ dateFrom, dateTo, force = false }) => {
      if (isFetchingEventsRef.current && !force) {
        return;
      }

      isFetchingEventsRef.current = true;
      setLoading(true);
      try {
        const { items } = await getAllData({
          client,
          query: GET_BOOKING_DATA_BY_SITE,
          dataPath: ["data", "getBookingDataBySite"],
          drillData: true,
          variables: {
            filter: buildGetBookingDataFilter({ dateFrom, dateTo }),
            dateFrom: dateTo,
            site: user.site,
            limit: 1000,
            nextToken: null,
          },
        });

        const newModifiedData = items.map((item) => formatBookingForDisplay(item));
        setEvents(newModifiedData);

        setPopoverObject((prev) => refreshPopoverObjectWithEvent(prev, newModifiedData));
      } finally {
        isFetchingEventsRef.current = false;
        setLoading(false);
      }
    },
    [client, user.site, buildGetBookingDataFilter, refreshPopoverObjectWithEvent],
  );

  const triggerFetch = useCallback(
    (shouldRefresh) => {
      if (!shouldRefresh || !currentDateRangeRef.current) {
        return;
      }

      const { dateFrom, dateTo } = currentDateRangeRef.current;
      loadCalendarEvents({ dateFrom, dateTo, force: true });
    },
    [loadCalendarEvents],
  );

  const onPageLoading = useCallback(
    async (event) => {
      const dateFrom = moment(event.firstDay).subtract(1, "days").utc().format();
      const dateTo = moment(event.lastDay).add(1, "days").utc().format();
      currentDateRangeRef.current = { dateFrom, dateTo };
      await loadCalendarEvents({ dateFrom, dateTo, force: false });
    },
    [loadCalendarEvents],
  );

  useEffect(() => {
    if (!shouldFetchData || !currentDateRangeRef.current) {
      return;
    }

    const { dateFrom, dateTo } = currentDateRangeRef.current;
    loadCalendarEvents({ dateFrom, dateTo, force: true });
  }, [shouldFetchData, loadCalendarEvents]);

  const [checkinEventNotFound, setCheckinEventNotFound] = useState(false);

  useEffect(() => {
    if (myEvents.length > 0 && !!eventID) {
      const event = myEvents.find((event) => event.id === eventID);

      if (!!event) {
        const eventData = {
          ...event,
          reservedForEmails: event?.bookedUsers?.map((k) => k.email),
        };
        // Regular check-in flow
        openDialog();
        setPopoverObject({
          equipmentData: event,
          eventData: eventData,
        });
      } else {
        setCheckinEventNotFound(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEvents, eventID]);

  const history = useHistory();

  const routeChange = (path) => {
    history.push(path);
  };

  return (
    <>
      <MainViewStyled>
        <>
          <BackdropPortal show={loading} />
          <CalendarContainer>
            <Page>
              <Eventcalendar
                theme="ios"
                renderHeader={renderMyHeader}
                themeVariant="light"
                clickToCreate={false}
                dragToCreate={false}
                dragToMove={false}
                dragToResize={false}
                eventDelete={false}
                data={myEvents}
                view={view}
                onEventClick={onEventClick}
                renderScheduleEvent={renderScheduleEvent}
                dataTimezone={dataTimeZone}
                displayTimezone={displayTimeZone}
                timezonePlugin={momentTimezone}
                eventOverlap={false}
                onPageLoading={onPageLoading}
              />
            </Page>
          </CalendarContainer>
          {isLegendVisible && <BookingScheduleStatus />}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "start", marginLeft: "4px" }}>
            <OwcHyperlink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsLegendVisible(!isLegendVisible);
              }}
            >
              {isLegendVisible ? "Hide legend" : "Show legend"}
            </OwcHyperlink>
          </div>
        </>
      </MainViewStyled>
      <PopOverModal
        title={Object.keys(popoverObject?.equipmentData).length > 0 && <PopoverBookingTitle data={popoverObject} />}
        closeTitle={"Close"}
        data={popoverObject}
        showEmailButton={false}
        showDuplicateButton={true}
        actions={renderMyBookingModalActions()}
        {...dialogProps}
        onDuplicateBooking={
          editBooking ? () => editBooking({ ...popoverObject?.equipmentData, isDuplicate: true }) : undefined
        }
        onDialogCloseCallBack={() => {
          setPopoverObject({
            equipmentData: {},
            eventData: {},
          });
        }}
      >
        <>
          <PopoverBookingStatusDetail
            data={popoverObject}
            equipmentDetails={true}
            allowAssistanceEdit={isBookingAdmin}
          />
        </>
      </PopOverModal>
      {checkinEventNotFound && (
        <OwcModalDialog visible={true} size={"sm"} className="owcmodalZIndex" disableBackdropClick={true}>
          <div slot="header">Error</div>
          <div>Booking event not found</div>

          <div
            slot="actions"
            style={{
              display: "flex",
              justifyContent: "end",
            }}
          >
            <OwcButton
              onClick={() => {
                setCheckinEventNotFound(false);
                routeChange("/bookings");
              }}
            >
              OK
            </OwcButton>
          </div>
        </OwcModalDialog>
      )}
      {isDropBooking && (
        <DeleteBooking
          dropType={dropType}
          setIsDropBooking={setIsDropBooking}
          setIsDialogOpen={setIsDialogOpen}
          isDialogOpen={isDialogOpen}
          item={bookingIdForAction}
          status={status}
          setStatus={setStatus}
          triggerFetch={triggerFetch}
        />
      )}
    </>
  );
};

export default compose(
  connect(null, {
    loadPendingReservations: loadPendingReservationsAction,
  }),
  withApollo,
)(BookingGrid);
